import React, {useEffect, useRef} from 'react';
import {Stack} from '@mui/material';
import MarkerItem from './marginRail/MarkerItem';
import {useStableMarkers} from './marginRail/useStableMarkers';
import {useMarginRailActive} from './marginRail/useMarginRailActive';
import {useRailIconMode} from '../hooks/useRailIconMode';
import useReferenceNumberStore from '../stores/useReferenceNumberStore';
import useYDocStore from '../../../stores/useYDocStore';
import RailModeContext from './RailModeContext';

// Static sx objects hoisted outside render to avoid allocating new objects on every paint.
const ICON_SX = {
    width: '100%',
    maxWidth: 48,
    marginRight: 0,
    pointerEvents: 'auto',
    zIndex: 0,
    position: 'relative',
    overflow: 'visible',
};

const FULL_SX = {
    width: '100%',
    maxWidth: 'var(--pm-margin-rail-width)',
    marginRight: 'var(--pm-margin-rail-gap)',
    pointerEvents: 'auto',
    zIndex: 0,
    position: 'relative',
};

/**
 * MarginRail
 * Renders Google-Docs-style markers (comment/reference/task) aligned to editor lines.
 * Determines its own display mode (full vs icon) by observing its parent grid cell width —
 * no dependency on parent container size or external state.
 */
export default function MarginRail({markers, active, activeReferenceUid = null, onMarkerClick, onMarkerDelete, onThreadSnapshot, view, reportId}) {
    // Keep callback refs up-to-date without causing re-renders (advanced-event-handler-refs).
    const stableOnMarkerClick = useRef(onMarkerClick);
    const stableOnMarkerDelete = useRef(onMarkerDelete);
    const stableOnThreadSnapshot = useRef(onThreadSnapshot);

    stableOnMarkerClick.current = onMarkerClick;
    stableOnMarkerDelete.current = onMarkerDelete;
    stableOnThreadSnapshot.current = onThreadSnapshot;

    const yReferenceMarks = useYDocStore(state => state.yReferenceMarks);
    const subscribeToReferences = useReferenceNumberStore(state => state.subscribeToReferences);

    useEffect(() => {
        if (!yReferenceMarks) return;
        return subscribeToReferences(yReferenceMarks);
    }, [yReferenceMarks, subscribeToReferences]);

    const stableMarkers = useStableMarkers(markers);

    const {railRef} = useMarginRailActive({
        active,
        activeReferenceUid,
        view,
        onClear: () => stableOnMarkerClick.current?.(null),
    });

    // Icon mode is derived from the parent grid cell width (see useRailIconMode).
    const isIconMode = useRailIconMode(railRef);

    return (
        <RailModeContext.Provider value={isIconMode ? 'icon' : 'full'}>
            <Stack
                ref={railRef}
                className="pm-margin-rail"
                data-rail-mode={isIconMode ? 'icon' : undefined}
                sx={isIconMode ? ICON_SX : FULL_SX}
            >
                {stableMarkers.map((m) => (
                    <MarkerItem
                        key={`${m.type}:${m.uid}`}
                        marker={m}
                        active={active}
                        onMarkerClickRef={stableOnMarkerClick}
                        onMarkerDeleteRef={stableOnMarkerDelete}
                        onThreadSnapshotRef={stableOnThreadSnapshot}
                        reportId={reportId}
                        view={view}
                    />
                ))}
            </Stack>
        </RailModeContext.Provider>
    );
}
